import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { difference, filter, forIn, isNil, keys, some, flatten } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchAll } from 'rxjs/operators';
import { FilterFieldDef, FilterFieldType, FilterFieldValue, FilterGridColDef, FilterQueryParams } from './covance-filter.models';
import { CovanceFilterBaseType } from './filter-type/covance-filter-base-type';
import { CovanceFilterCheckboxType } from './filter-type/covance-filter-checkbox-type';
import { CovanceFilterDateRangeType } from './filter-type/covance-filter-date-range-type';
import { CovanceFilterDateType } from './filter-type/covance-filter-date-type';
import { CovanceFilterMultiSelectType } from './filter-type/covance-filter-multiselect-type';
import { CovanceFilterNumberRangeType } from './filter-type/covance-filter-number-range-type';
import { CovanceFilterNumberType } from './filter-type/covance-filter-number-type';
import { CovanceFilterSelectType } from './filter-type/covance-filter-select-type';
import { CovanceFilterTextType } from './filter-type/covance-filter-text-type';
import { CovanceFilterAgeRangeType } from './filter-type/covance-filter-age-range-type';
import { CovanceFilterAgeType } from './filter-type/covance-filter-age/covance-filter-age-type';
import { CovanceFilterDateTimeRangeType } from './filter-type/covance-filter-date-time-range/covance-filter-date-time-range-type';

export interface SerializedFilter {
    title: string;
    value: string;
}

@Injectable()
export class CovanceFilter {

    constructor(
        private route: ActivatedRoute,
        private filterTextType: CovanceFilterTextType,
        private filterNumberType: CovanceFilterNumberType,
        private filterNumberRangeType: CovanceFilterNumberRangeType,
        private filterDateType: CovanceFilterDateType,
        private filterDateRangeType: CovanceFilterDateRangeType,
        private filterSelectType: CovanceFilterSelectType,
        private filterMultiSelectType: CovanceFilterMultiSelectType,
        private filterAgeRangeType: CovanceFilterAgeRangeType,
        private filterCheckboxType: CovanceFilterCheckboxType,
        private filterAgeType: CovanceFilterAgeType,
        private filterDateTimeRangeType: CovanceFilterDateTimeRangeType,
        private router: Router) { }

    public listen(fieldVals: FilterFieldValue[]): Observable<Params> {
        const result = this.route.queryParams
            .pipe(
                distinctUntilChanged(),
                map((queryParams: Params) => {
                    const covanceFilterQueryParams: Params = {}; 
                    fieldVals.forEach(fieldVal => {
                        const queryParam = fieldVal.def.queryParam;
                        fieldVal.queryParamValue = null;
                        const urlParamValue = queryParams[queryParam] || null;
                        if (fieldVal.def.queryParam && fieldVal.queryParamValue !== urlParamValue) {
                            if (urlParamValue) {
                                covanceFilterQueryParams[fieldVal.def.queryParam] = urlParamValue;
                            }
                            
                            fieldVal.queryParamValue = urlParamValue;
                            if (fieldVal.def.onChange) {
                                fieldVal.def.onChange(fieldVal);
                            }
                        }
                        const filterType = this.getFilterType(fieldVal.def.type);
                        filterType.setValue(fieldVal);
                    });

                    return covanceFilterQueryParams;
                }),
            );
        return result;
    }

    public queryParamsToString(queryParams: Params): string {
        const paramsUrl = keys(queryParams)
            .map((key: string) => `${key}=${encodeURIComponent(queryParams[key])}`)
            .join('&');

        return paramsUrl;
    }

    public getObservableQueryParams(queryParams: string[]): Observable<FilterQueryParams> {
        let params: Params = null;

        return this.route.queryParams
            .pipe(
                map((allParams: Params) => {
                    let isChanged = false;

                    if (params == null) {
                        params = {};
                        isChanged = true;
                    }

                    forIn(
                        allParams,
                        (value: string, key: string) => {
                            if (some(queryParams, (queryParam: string) => queryParam === key) &&
                                params[key] !== value) {
                                params[key] = value;
                                isChanged = true;
                            }
                        });

                    difference(keys(params), keys(allParams)).forEach(key => {
                        delete params[key];
                        isChanged = true;
                    });

                    if (!isChanged) {
                        return of<FilterQueryParams>();
                    }

                    return of({
                        observableQueryParams: params,
                        allQueryParams: allParams
                    } as FilterQueryParams);
                }),
                switchAll()
            );
    }

    public getFilteredData<T>(list: Observable<T[]>, fieldVals: FilterFieldValue[]): Observable<T[]> {
        const observableQueryParams = this.getObservableQueryParams(fieldVals.map(q => q.def.queryParam));

        return combineLatest(list, observableQueryParams)
            .pipe(
                map(([entities, params]: [T[], FilterQueryParams]) => {
                    if (!entities) {
                        return null;
                    }

                    fieldVals.forEach(fieldVal => {
                        const filterType = this.getFilterType(fieldVal.def.type);
                        filterType.setItems(fieldVal, entities || []);

                        const value = params.allQueryParams[fieldVal.def.queryParam];
                        fieldVal.queryParamValue = value;
                        filterType.setValue(fieldVal);

                        if (!isNil(value) && !fieldVal.def.isServerSideFilter) {
                            entities = filter(entities, (entity: T) => {
                                if (fieldVal.def.isMatch) {
                                    return fieldVal.def.isMatch(fieldVal.value, entity);
                                }
                                const item = entity as any;
                                return filterType.isMatch(fieldVal, item[fieldVal.def.queryParam]);
                            });
                        }
                    });

                    return entities;
                }));
    }

    public setFiltersItems<T>(list: T[], fieldVals: FilterFieldValue[]) {
        let items = [...(list || [])];
        fieldVals.forEach(fieldVal => {
            if (fieldVal.def.type === FilterFieldType.External) {
                return;
            }
            const filterType = this.getFilterType(fieldVal.def.type);
            filterType.setItems(fieldVal, items || []);
            if (fieldVal.def.leaveOnlySelectedItems) {
                const selectedItems = flatten([...[fieldVal.value || fieldVal.items || []]]).map((i: any) => i && i.key);
                items = items.filter((item: any) => {
                    const itemKey = item[fieldVal.def.queryParam] && item[fieldVal.def.queryParam].key;
                    return selectedItems.indexOf(itemKey) !== -1;
                });
            }
        });
    }

    public getFieldValues(cols: FilterGridColDef[]): FilterFieldValue[] {
        const fieldVals = cols
            .filter(col => !col.filterFieldDef || (col.filterFieldDef && !col.filterFieldDef.isOff))
            .map(col => {
                const fieldDef: FilterFieldDef = {
                    title: col.headerName,
                    queryParam: col.field,
                    type: FilterFieldType.Text
                };

                const def = col.filterFieldDef as any;
                if (def) {
                    forIn(def, (value: string, key: string) => {
                        if (!isNil(def[key])) {
                            (fieldDef as any)[key] = value;
                        }
                    });
                }

                return { def: fieldDef };
            });

        return fieldVals;
    }

    public serializeFieldValues(fieldVals: FilterFieldValue[]): SerializedFilter[] {
        return fieldVals.map(fieldVal => ({
            title: fieldVal.def.title,
            value: fieldVal
                ? this.getFilterType(fieldVal.def.type).serializeValue(fieldVal.value)
                : ''
        }));
    }

    public resetFilter() {
        this.router.navigate([], {
            queryParams: [],
            relativeTo: this.route
        });
    }

    public setValue(fieldVal: FilterFieldValue, value: any) {
        fieldVal.value = value;
        const theFilter = this.getFilterType(fieldVal.def.type);
        theFilter.setQueryParamValue(fieldVal);
        theFilter.onFieldChange(fieldVal);
    }

    private getFilterType(type: FilterFieldType): CovanceFilterBaseType {
        switch (type) {
            case FilterFieldType.Text:
                return this.filterTextType;
            case FilterFieldType.Number:
                return this.filterNumberType;
            case FilterFieldType.NumberRange:
                return this.filterNumberRangeType;
            case FilterFieldType.Date:
                return this.filterDateType;
            case FilterFieldType.DateRange:
                return this.filterDateRangeType;
            case FilterFieldType.DateTimeRange:
                return this.filterDateTimeRangeType;
            case FilterFieldType.Select:
                return this.filterSelectType;
            case FilterFieldType.MultiSelect:
                return this.filterMultiSelectType;
            case FilterFieldType.Checkbox:
                return this.filterCheckboxType;
            case FilterFieldType.AgeRange:
                return this.filterAgeRangeType;
            case FilterFieldType.Age:
                return this.filterAgeType;
            default:
                return this.filterTextType;
        }
    }
}
